import AsyncStorage from '@react-native-async-storage/async-storage';
import { createClient } from '@supabase/supabase-js';
import { Platform } from 'react-native';
import 'react-native-url-polyfill/auto';

import { isRecoveryHash } from './recovery-link';
import { chunkedSecureStorageAdapter } from './secure-storage-adapter';

// Arm the password-recovery flow from the URL BEFORE the client initialises:
// the PASSWORD_RECOVERY event it emits fires before the auth hook can
// subscribe (issue #449). use-auth-state.ts seeds from this same key.
if (Platform.OS === 'web' && typeof window !== 'undefined' && isRecoveryHash(window.location.hash)) {
  window.sessionStorage.setItem('nearkin.password-recovery', '1');
}

const url = process.env.EXPO_PUBLIC_SUPABASE_URL;
const anonKey = process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY;

if (!url || !anonKey) {
  throw new Error('EXPO_PUBLIC_SUPABASE_URL and EXPO_PUBLIC_SUPABASE_ANON_KEY must be set -- see .env.example.');
}

// Issue #125: session persistence used to go straight to AsyncStorage
// on every platform -- unencrypted storage, readable by anything with
// filesystem access to the app's sandbox. Native now uses
// expo-secure-store (the platform keystore) via
// secure-storage-adapter.ts's chunked wrapper, which is the real fix
// for that.
//
// Web keeps AsyncStorage (which is itself just a localStorage wrapper
// on web -- confirmed via @react-native-async-storage/async-storage's
// own web implementation) -- there's no browser-native equivalent of
// a platform keystore a web app can reach; expo-secure-store's own web
// shim doesn't provide real hardware/OS-backed security either,
// it would just be AsyncStorage again with extra indirection. This
// matches Supabase's own documented guidance for Expo apps: SecureStore
// on native, plain storage on web.
//
// This session-loading path is also why app.json sets
// `web.output: "single"` (client-side SPA) instead of the Expo Router
// default `"static"` (SSR): Supabase's session bootstrap touches
// `window` via AsyncStorage, which crashes during Node-side SSR. SSR's
// main benefit is SEO, which this app gets little of anyway -- most
// screens sit behind the auth gate, and the separate marketing/landing
// site already owns SEO. Client-side rendering avoids guarding every
// browser-only call for a server environment that buys us nothing here.
export const supabase = createClient(url, anonKey, {
  auth: {
    storage: Platform.OS === 'web' ? AsyncStorage : chunkedSecureStorageAdapter,
    autoRefreshToken: true,
    persistSession: true,
    // OAuth's redirect-based flow (signInWithOAuth) sends the browser
    // to Google and back; on web, the returning URL carries the session
    // in its fragment/query and needs this to auto-parse it. On native
    // there's no browser URL to detect a session from -- that flow uses
    // a deep link + WebBrowser instead, not built yet (see issue #11's
    // native gap).
    detectSessionInUrl: Platform.OS === 'web',
  },
});
